import { useState, useEffect } from 'react';
import { Hero } from '../types';
import { supabase } from '../lib/supabaseClient';
import { getPublicStorageUrl } from '../utils/storage';
import { toCamelCase } from '../utils/caseConversion';

export const useHeroes = () => {
  const [heroes, setHeroes] = useState<Hero[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHeroes = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 Chargement des héros depuis Supabase...');

        // Charger les héros depuis Supabase (le client Supabase gère son propre timeout)
        const { data: heroesData, error: heroesError } = await supabase
          .from('elements')
          .select('*')
          .eq('type', 'hero')
          .order('created_at', { ascending: false });

        if (heroesError) {
          throw new Error(`Erreur Supabase: ${heroesError.message}`);
        }

        // Convert snake_case from database to camelCase for JavaScript
        const heroesDataCamelCase = toCamelCase(heroesData);

        // Transform Supabase format to Hero interface
        const transformedHeroes: Hero[] = (heroesDataCamelCase || []).map((hero: any) => ({
          id: hero.id,
          name: hero.translations.fr.name, // Default to French
          emoji: getPublicStorageUrl(hero.assetUrl), // Now in camelCase
          unlocked: hero.unlocked,
          bookId: hero.rewardFrom?.bookId, // Now in camelCase
          hasBeenPublished: hero.hasBeenPublished || false // A été publié dans une histoire visible
        }));

        console.log(`✅ ${transformedHeroes.length} héros chargés depuis Supabase`);
        setHeroes(transformedHeroes);
        
      } catch (err) {
        console.error('❌ Erreur lors du chargement des héros:', err);
        console.error('🔍 Détails:', {
          message: err instanceof Error ? err.message : 'Erreur inconnue',
          stack: err instanceof Error ? err.stack : undefined,
          supabaseUrl: process.env.REACT_APP_SUPABASE_URL
        });
        setError(err instanceof Error ? err.message : 'Erreur inconnue');

        // Fallback to empty array
        setHeroes([]);
      } finally {
        console.log('✅ useHeroes: loading terminé');
        setLoading(false);
      }
    };

    fetchHeroes();
  }, []);

  return {
    heroes,
    loading,
    error,
    refetch: () => {
      setLoading(true);
      setError(null);
      // Re-run the effect
      window.location.reload();
    }
  };
};

export default useHeroes;